import React, { useState } from "react";
import { ai } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TONES = ["Heartfelt", "Playful", "Formal", "Cinematic"];

export default function AICompose({ title, message, occasion, recipientName, onTitle, onMessage }) {
  const [mode, setMode] = useState("draft"); // draft | titles | rewrite
  const [tone, setTone] = useState("Heartfelt");
  const [descr, setDescr] = useState("");
  const [busy, setBusy] = useState(false);
  const [titles, setTitles] = useState([]);

  const draft = async () => {
    if (!recipientName || !occasion.trim()) {
      toast.error("Fill recipient name and describe the occasion");
      return;
    }
    setBusy(true);
    try {
      const { message: m } = await ai.draft({ occasion, recipient_name: recipientName, tone });
      onMessage(m);
      toast.success("Draft ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable");
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    const seed = descr || occasion || title;
    if (!seed) {
      toast.error("Type a short description first");
      return;
    }
    setBusy(true);
    try {
      const { titles: t } = await ai.titles({ description: seed });
      setTitles(t);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable");
    } finally {
      setBusy(false);
    }
  };

  const rewrite = async () => {
    if (!message?.trim()) {
      toast.error("Write a draft note first, then rewrite it");
      return;
    }
    setBusy(true);
    try {
      const { rewritten } = await ai.rewrite({ text: message, tone });
      onMessage(rewritten);
      toast.success(`Rewritten in ${tone.toLowerCase()} tone`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-terracotta/25 bg-vault-800/60 p-5" data-testid="ai-compose">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-3.5 h-3.5 text-terracotta" strokeWidth={1.5} />
        <p className="label-mono text-terracotta">AI Compose · GPT-5.6</p>
      </div>

      <div className="flex gap-1 mb-4 text-xs">
        {[
          { k: "draft", label: "Draft note" },
          { k: "titles", label: "Suggest titles" },
          { k: "rewrite", label: "Rewrite tone" },
        ].map((m) => (
          <button
            key={m.k}
            type="button"
            onClick={() => setMode(m.k)}
            data-testid={`ai-tab-${m.k}`}
            className={`px-3 py-1.5 border transition-colors ${
              mode === m.k ? "border-terracotta text-terracotta" : "border-white/10 text-white/50 hover:text-bone"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === "draft" && (
        <div className="space-y-3">
          <p className="text-xs text-white/50">Generates a {tone.toLowerCase()} note for {recipientName || "your recipient"} using the occasion field.</p>
          <div>
            <Label className="label-mono mb-2 block">Tone</Label>
            <ToneSelector tone={tone} setTone={setTone} />
          </div>
          <Button type="button" onClick={draft} disabled={busy} data-testid="ai-draft-btn" className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Draft note with AI"}
          </Button>
        </div>
      )}

      {mode === "titles" && (
        <div className="space-y-3">
          <Label className="label-mono block">A short description</Label>
          <Input
            value={descr}
            onChange={(e) => setDescr(e.target.value)}
            placeholder="A message for my daughter on her 18th"
            data-testid="ai-descr"
          />
          <Button type="button" onClick={suggest} disabled={busy} data-testid="ai-titles-btn" className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suggest 3 titles"}
          </Button>
          {titles.length > 0 && (
            <div className="space-y-2 pt-2" data-testid="ai-titles-list">
              {titles.map((t, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => { onTitle(t); toast.success("Title applied"); }}
                  className="w-full text-left px-3 py-2 border border-white/10 hover:border-terracotta/50 text-sm font-serif text-bone transition-colors"
                  data-testid={`ai-title-option-${i}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "rewrite" && (
        <div className="space-y-3">
          <p className="text-xs text-white/50">Takes your current Note and rewrites it in the selected tone.</p>
          <div>
            <Label className="label-mono mb-2 block">Target tone</Label>
            <ToneSelector tone={tone} setTone={setTone} />
          </div>
          <Button type="button" onClick={rewrite} disabled={busy} data-testid="ai-rewrite-btn" className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Rewrite in ${tone}`}
          </Button>
        </div>
      )}
    </div>
  );
}

function ToneSelector({ tone, setTone }) {
  return (
    <div className="grid grid-cols-2 gap-1">
      {TONES.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => setTone(t)}
          data-testid={`tone-${t.toLowerCase()}`}
          className={`px-2 py-2 text-xs border transition-colors ${
            tone === t ? "border-terracotta text-terracotta bg-terracotta/5" : "border-white/10 text-white/60 hover:text-bone"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}
